import React, {useMemo, useRef, useState} from "react";
import { Link, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import TalentPoolNavbar from "./components/TalentPoolNavbar";
import SidebarComponent from "../../components/SidebarComponent";
import InviteByEmailModal from "./components/InviteByEmailModal";
import InviteByGroupModal from "./components/InviteByGroupModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAPI } from "../../config/API";
import { useAuth } from "../../providers/AuthProvider";
import CreateUpdateTalentPoolModal from "./components/CreateUpdateTalentPoolModal";
import LoadingBanner from "../../components/LoadingBanner";
import TalentPool from "../../models/TalentPool";
import {useMount} from "react-use";

const LOOKUP = {
    "favorites": -1,
    "past-hires": -2,
    "bring-your-own-talent": -3,
    "blocked-talent": -10,
}

const GENERAL_DESCRIPTIONS = {
    '-1': 'This pool contains all talent that have been favorited with a heart',
    '-2': 'This pool contains all talent that have been hired in the past',
    '-3': 'This pool contains the talent you have invited to join Wripple',
    '-10': 'Talent added to this pool will be blocked from viewing your job postings and will not appear in your talent searches',
}
const TalentPools = () => {

    const params = useParams();

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [editingPool, setEditingPool] = useState(false)
    const { auth } = useAuth();

    const talentScaleRef = useRef(null);
    const location = useLocation();
    //
    // ALL TALENT POOLS
    //
    const {
        data: talentPoolsData,
        isLoading,
        error,
        refetch
    } = useAPI(['fetchTalentPools', {clientId: auth.clientOrg?.()?.id}], {
        onSuccess: async data => {
            // console.log(data, 'DATA ON SUCESSSS')
        },
    })

    const groupedPools = useMemo(() => {
        if (isLoading || !talentPoolsData) {
            return [];
        }
        return TB.groupBy(talentPoolsData, item => {
            if (item.is_default) return 'general';
            if (item.is_personal) return 'personal';
            return 'company'
        });
    }, [talentPoolsData, isLoading]);

    const selectedPool = useMemo(() => {

        if (!talentPoolsData?.length) return null;

        let sP = talentPoolsData?.find(item => item.id === (LOOKUP[params.talentPoolId] ?? parseInt(params.talentPoolId)))
        if (!sP && !!params.talentPoolId) {
            TB.toast("Talent Pool Not Found", { theme: 'danger' });
            return navigate('/client/talent-pool', { replace: true });
        }
        if (!sP) {
            return null
        }
        if (!sP.description) {
            sP.description = GENERAL_DESCRIPTIONS[sP.id] || null
        }
        return sP;

    }, [location.pathname, isLoading, talentPoolsData]);


    const deleteTalentPoolMutation = useMutation(
        ['deleteTalentPoolMutation'], id => TalentPool.deleteTalentPool(id),
        {
            onSuccess: async () => {
                navigate('/client/talent-pool', { replace: true });
                await TB.wait(200)

                await queryClient.invalidateQueries(['fetchTalentPools', {clientId: auth.clientOrg?.()?.id}]);
                TB.toast("Talent Pool Deleted", { type: 'success' });

                return true;
            },
            onError: () => {
                TB.showServerErrorToast();
                return false;
            }
        }
    )

    const onDeleteTalentPool = async pool => {
        if (!pool?.canBeEdited()) {
            return TB.showPermissionError();
        }
        const shouldDelete = await TB.alert(
            'error',
            `Remove ${pool.name}?`,
            `Are you sure you want to remove the pool ${pool.name}? This cannot be undone`,
            [{ title: 'Yes, Remove' }, { title: 'Cancel' }],
        );

        if (shouldDelete) {
            return deleteTalentPoolMutation.mutate(pool.id);
        }
    }

    const scaleElementWidth = (element) => {
        // Use getComputedStyle to get the computed width of the element
        const computedStyle = window.getComputedStyle(element);
        const currentWidth = computedStyle.width;

        console.log('scaleElementWidth', )
        let hasCustomWidth = $(element)[0].className.includes('w-[');
        // Check if the width is defined and is not a percentage
        if (hasCustomWidth ||
            (element.style.width && element.style.width.includes('px'))) {

            // Parse the current width as a number
            const currentWidthValue = parseFloat(currentWidth || element.offsetWidth);

            // Calculate the new scaled width (90% of current width)
            const scaledWidth = currentWidthValue * 0.9;

            console.log('scaleElementWidth', scaledWidth)
            // Set the new width to the element as inline style
            element.style.width = `${scaledWidth}px`;
        }

        // Recursively scale the width of child elements
        Array.from(element.children).forEach(child => scaleElementWidth(child));
    }
    useMount(async () => {
        await TB.wait(3000);
        // if (talentScaleRef.current) {
        //     console.log('casdkclm', talentScaleRef)
        //     scaleElementWidth(document.querySelector('.talent-scaling'));
        // }

    })


    const renderButtons = () => {
        if (selectedPool?.id === -3) { // is on talent invitations page
            return (
                <>
                    <InviteByEmailModal />
                    <InviteByGroupModal />
                </>
            )
        }

        if (selectedPool?.id > 0 || selectedPool?.isBlockedTalentPool?.()) {
            return (
                <>
                    {selectedPool.canAddRemoveTalent() && (
                        <Link
                            type="button"
                            to={`/client/talent-pool/${selectedPool?.isBlockedTalentPool?.() ? 'blocked-talent' : selectedPool.id}/add-talent`}
                            className="btn bg-white p-3 duration-300 hover:bg-black hover:text-white flex gap-2 whitespace-nowrap"
                        >
                            <i className="fas fa-plus" /> Add Talent
                        </Link>
                    )}

                    {selectedPool?.canBeEdited() && (
                        <button
                            type="button"
                            className="btn bg-white p-3 duration-300 hover:bg-black hover:text-white flex gap-2 whitespace-nowrap"
                            onClick={() => {
                                setEditingPool(selectedPool)
                            }}
                        >
                            <i className="far fa-edit"/> Edit Pool
                        </button>
                    )}

                    {editingPool && editingPool?.canBeEdited() && (
                        <CreateUpdateTalentPoolModal
                            pool={editingPool}
                            onClose={() => setEditingPool(false)}/>
                    )}


                    {!selectedPool?.isWrippleRecommendedPool?.() && !selectedPool?.isBlockedTalentPool?.() && (
                        <button
                            type="button"
                            className="btn bg-white p-3 duration-300 hover:bg-black hover:text-white flex gap-2 whitespace-nowrap"
                            onClick={() => onDeleteTalentPool(selectedPool)}
                        >
                            <i className="fas fa-ban"/> Delete Pool
                        </button>
                    )}
                </>
            )
        }

        return null;
    }


    if (location.pathname?.includes('talent-profiles')) {
        return <Outlet/>
    }

    if (location.pathname?.includes('add-talent')) {
        return (
            <div className="flex flex-col flex-1 talent-scaling">
                <div className="flex flex-1">
                <Outlet />
                </div>
            </div>
        )
    }
    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center pt-10">
                <LoadingBanner message="Loading Talent Pools"/>
            </div>
        )
    }


    console.log('TalentPools')
    return (
        <div className="flex flex-col flex-1 talent-scaling" ref={talentScaleRef}>

            <div className="flex flex-1">
                <div className="w-full">
                    <div className="h-[75px] bg-black">
                        <div className="flex h-full">
                            <div className="flex-1 p-4 flex flex-col justify-center">
                                <p className="text-sm text-gray-400">Build Your Own Team</p>
                                <p className="text-xl text-white font-bold">Manage Talent Pools</p>
                            </div>
                            <div className="flex-1" />
                        </div>
                    </div>

                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center pt-10">
                            <LoadingBanner message="Loading Talent Pools"/>
                        </div>
                    ) : (
                        <React.Fragment>
                            <div
                                className="bg-wripplePrimaryDarkPurple py-3 px-4 w-full min-h-[67px] flex items-center">
                                <div className="flex flex-col sm:flex-row items-center justify-between gap-x-8 gap-y-4 w-full">
                                    <div className="flex items-center flex-wrap">
                                        <p className="text-2xl text-white mr-6">{selectedPool?.name || 'Talent Pools'}</p>
                                        <p className="text-white line-clamp-2 text-ellipsis">{selectedPool?.description || 'Welcome to Talent Pools'}</p>
                                    </div>
                                    <div className="flex items-center gap-6">
                                        {renderButtons()}
                                    </div>
                                </div>
                            </div>

                            <div className="flex h-full">
                                <SidebarComponent canClose>
                                    <TalentPoolNavbar
                                        groupedPools={groupedPools}
                                        isLoading={isLoading}
                                    />
                                </SidebarComponent>

                                <div className="flex-1 py-12 md:px-10 px-4 relative" id="talent-pool-manage-container">
                                    {!isLoading && <Outlet/>}
                                </div>
                            </div>
                        </React.Fragment>
                    )}
                </div>
            </div>
        </div>
    )
}

export default TalentPools;